import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle, ArrowLeft, Loader2, PlusCircle, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import { useAdminTasks } from '../hooks/useAdminTasks';

export function AdminTaskListPage() {
  const { tasks, total, loading, error, refresh, hasMore, loadMore } = useAdminTasks();
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleDelete = async (taskId: string, taskName: string) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete the task "${taskName}"? This action cannot be undone.`,
    );
    if (!confirmed) return;

    setDeletingId(taskId);
    setDeleteError(null);
    try {
      await api.adminDeleteLitLegionTask(taskId);
      refresh();
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete task');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div>
        <Link
          to="/a/arche_lit_legion/admin"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-2"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Admin Dashboard
        </Link>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Manage Tasks</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {total} task{total !== 1 ? 's' : ''} total
            </p>
          </div>
          <Link to="/a/arche_lit_legion/admin/tasks/create">
            <Button>
              <PlusCircle className="h-4 w-4 mr-2" />
              Create Task
            </Button>
          </Link>
        </div>
      </div>

      {error && (
        <Card className="border-destructive">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              Error
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      )}

      {deleteError && <p className="text-sm text-destructive">{deleteError}</p>}

      {loading && tasks.length === 0 ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-20 rounded-lg" />
          ))}
        </div>
      ) : tasks.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-muted-foreground mb-4">No tasks created yet.</p>
          <Link to="/a/arche_lit_legion/admin/tasks/create">
            <Button variant="outline">
              <PlusCircle className="h-4 w-4 mr-2" />
              Create your first task
            </Button>
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {tasks.map((task) => (
            <Card key={task.taskId}>
              <CardContent className="flex items-center justify-between py-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-medium truncate">{task.name}</p>
                    {task.taskType && (
                      <Badge variant="secondary" className="text-xs shrink-0">
                        {task.taskType}
                      </Badge>
                    )}
                    {task.category && (
                      <Badge variant="outline" className="text-xs shrink-0">
                        {task.category}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <span>{task.xpReward} XP</span>
                    {task.npcId && <span>Has NPC</span>}
                    {task.parentId && <span>Has parent</span>}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-destructive hover:text-destructive shrink-0 ml-2"
                  onClick={() => handleDelete(task.taskId, task.name)}
                  disabled={deletingId === task.taskId}
                >
                  {deletingId === task.taskId ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                </Button>
              </CardContent>
            </Card>
          ))}

          {hasMore && (
            <div className="text-center">
              <Button variant="ghost" onClick={loadMore} disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Loading...
                  </>
                ) : (
                  'Load more'
                )}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
